/**
 * Overview ページ (ヒーロー + 統計)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business Design Guide のトップページ。グラデ見出しとチェック項目数・Principle数・カラー数の統計を表示。
 * @graph-connects api [calls] guide.get による集約取得
 */
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useBrandScope } from '../components/brand-scope-context.js';

/**
 * Overview ページ。
 * @returns ページ要素
 * @graph-connects api [calls] guide.get による集約取得
 */
export function Dashboard(): JSX.Element {
  const brandScope = useBrandScope();
  const guideQuery = useQuery(
    orpc.guide.get.queryOptions({
      input: brandScope.scope === '' ? {} : { brand: brandScope.scope },
    }),
  );
  const stats = guideQuery.data?.stats;

  const statItems: { value: number | undefined; label: string }[] = [
    { value: stats?.checklistItemCount, label: 'Check Items' },
    { value: stats?.principleCount, label: 'Principles' },
    { value: stats?.colorTokenCount, label: 'Color Tokens' },
  ];

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -right-20 -top-24 h-96 w-96 rounded-full hero-glow" />
      <div className="mb-4 text-caption font-semibold uppercase tracking-label text-accent-text">
        Design System
      </div>
      <h1 className="max-w-xl text-4xl font-extrabold leading-tight tracking-tight text-foreground">
        ブランド準拠の
        <br />
        <span className="bg-gradient-to-br from-accent to-highlight bg-clip-text text-transparent">
          判断基準
        </span>
        を、ここに。
      </h1>
      <p className="mt-4 max-w-lg text-title leading-relaxed text-subtle">
        Brand Core、UX Principles、チェックリスト、カラートークン、フォント規約、判定ロジック、
        AIプロンプトをデータベースで管理。人が見て、AIが読んで、いつでも更新できる。
      </p>
      <div className="mt-8 flex gap-8">
        {statItems.map((stat) => (
          <div key={stat.label} className="text-center">
            <div className="font-mono text-3xl font-extrabold tracking-tight text-foreground">
              {stat.value ?? '–'}
            </div>
            <div className="mt-1 text-caption uppercase tracking-label text-muted">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
